/* helper.ts */

/* d3.js */
import * as d3 from "d3";

/* Utils */
import { getConsistentColor } from "src/utils/helper/charts/colorMapper";
import { transform } from "src/utils/helper/string-transform/StringTransformer";

/* Types */
import { SVGSelection } from "src/shared/types/d3";

/* Constants */
import { CustomFeature } from "src/shared/constants/common/CustomFeature";

/* Interfaces */
import {
  CustomSankeyGraph,
  CustomSankeyNode,
} from "src/shared/interfaces/charts/SankeyDiagram";

import { SankeyTooltipProps } from "src/shared/interfaces/ChartTooltip";
import { ChartDimensionsProps } from "src/shared/interfaces/charts/BaseChart";

interface AnnotationProps {
  label: string;
  x: number;
}

const DURATION: number = 250;

/**
 * Create a consistent color for a given label.
 * The color is independent from the treatment line.
 */
export const createColors = (
  label: string,
  customFeature?: CustomFeature
): string => {
  switch (customFeature) {
    case CustomFeature.TREATMENT:
      return getConsistentColor(
        transform(label)
          .stripUnitSuffix()
          .stripLabelSuffix()
          .toString()
      );
    case CustomFeature.MEDICATION:
      return getConsistentColor(
        transform(label)
          .capitalizeFirstLetter()
          .stripUnitSuffix()
          .stripLabelSuffix()
          .toString()
      );
    default:
      return getConsistentColor(label);
  }
};

export const createTransformedLabel = (
  label: string,
  customFeature?: CustomFeature
) => {
  switch (customFeature) {
    case CustomFeature.TREATMENT:
      return transform(label)
        .stripUnitSuffix()
        .stripLabelSuffix()
        .toString();
    case CustomFeature.MEDICATION:
      return transform(label)
        .capitalizeFirstLetter()
        .stripUnitSuffix()
        .stripLabelSuffix()
        .toString();
    default:
      return label;
  }
};

export const createViewBox = (
  svg: SVGSelection,
  dimensions: ChartDimensionsProps
) => {
  const { height, width } = dimensions;
  return svg.attr("viewBox", `0 0 ${width} ${height}`);
};

export const createGradient = (
  svg: SVGSelection,
  data: CustomSankeyGraph,
  gradientPrefix: string,
  customFeature?: CustomFeature
) => {
  const defs = svg.append("defs");

  data.links.map((d, i) => {
    const gradientId = `${gradientPrefix}-gradient-${i}`;

    const gradient = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", d.source.x1)
      .attr("x2", d.target.x0);

    gradient
      .append("stop")
      .attr("offset", "50%")
      .attr(
        "stop-color",
        createColors(d.source.name, customFeature)
      );

    gradient
      .append("stop")
      .attr("offset", "100%")
      .attr(
        "stop-color",
        createColors(d.target.name, customFeature)
      );
  });
};

export const createAnnotations = (
  svg: SVGSelection,
  data: CustomSankeyGraph,
  maxDepth: number,
  dimension: ChartDimensionsProps
) => {
  const groupedNodes: {
    [key: number]: CustomSankeyNode[];
  } = {};

  data.nodes.map((node) => {
    if (!groupedNodes[node.depth!]) {
      groupedNodes[node.depth!] = [];
    }
    groupedNodes[node.depth!].push(node);
  });

  const annotations: AnnotationProps[] = [];

  for (let i = 0; i < maxDepth; i++) {
    if (groupedNodes[i] && groupedNodes[i + 1]) {
      const leftNodes = groupedNodes[i];
      const rightNodes = groupedNodes[i + 1];

      const avgLeftX = d3.mean(leftNodes, (d) => d.x1);
      const avgRightX = d3.mean(rightNodes, (d) => d.x0);

      annotations.push({
        label: `TL${i + 1}`,
        x: (avgLeftX! + avgRightX!) / 2,
      });
    }
  }

  svg
    .selectAll(".tl-annotation")
    .data(annotations)
    .join("text")
    .attr("x", (d) => d.x)
    .attr("y", dimension.height + 32)
    .attr("text-anchor", "middle")
    .text((d) => d.label)
    .attr("fill", "#aab9ce")
    .attr("font-size", "12px");
};

export const setTooltipData = (
  tooltipData: SankeyTooltipProps,
  customFeature?: CustomFeature
) => {
  if (!tooltipData) return [];

  const { source, target, value } = tooltipData;

  return [
    {
      key: "Source",
      value: createTransformedLabel(source, customFeature),
    },
    {
      key: "Target",
      value: createTransformedLabel(target, customFeature),
    },
    { key: "Value", value: value },
  ];
};

export const highlightConnectedNode = (
  svg: SVGSelection,
  clickedNode: CustomSankeyNode,
  diagramId: string
): void => {
  const clickedNodeId = `${clickedNode.name}@${clickedNode.depth}`;

  const connectedLinks: Set<string> = new Set();
  const visibleNodeIds: Set<string> = new Set();

  svg
    .selectAll<SVGPathElement, any>(`.${diagramId} .link`)
    .each((d) => {
      const sourceId = `${d.source.name}@${d.source.depth}`;
      const targetId = `${d.target.name}@${d.target.depth}`;

      if (
        sourceId === clickedNodeId ||
        targetId === clickedNodeId
      ) {
        const flowId = `${sourceId}→${targetId}`;
        connectedLinks.add(flowId);
        visibleNodeIds.add(sourceId);
        visibleNodeIds.add(targetId);
      }
    });

  svg
    .selectAll<SVGPathElement, any>(`.${diagramId} .link`)
    .transition()
    .duration(DURATION)
    .style("opacity", (d) => {
      const id = `${d.source.name}@${d.source.depth}→${d.target.name}@${d.target.depth}`;
      return connectedLinks.has(id) ? 1 : 0.1;
    });

  svg
    .selectAll<SVGRectElement, CustomSankeyNode>(
      `.${diagramId} .node`
    )
    .transition()
    .duration(DURATION)
    .style("opacity", (d) => {
      const id = `${d.name}@${d.depth}`;
      return visibleNodeIds.has(id) ? 1 : 0.1;
    });

  svg
    .selectAll(`.${diagramId} text.label`)
    .transition()
    .duration(DURATION)
    .style("fill-opacity", (d: any) => {
      const id = `${d.name}@${d.depth}`;
      return visibleNodeIds.has(id) ? 1 : 0.1;
    });
};

/**
 * Highlight a flow in the diagram when user clicks on a link.
 */
export const highlightFlow = (
  svg: SVGSelection,
  flowId: string,
  diagramId: string
): void => {
  const [sourceId, targetId] = flowId.split("→");

  svg
    .selectAll<SVGPathElement, any>(`.${diagramId} .link`)
    .transition()
    .duration(DURATION)
    .style("opacity", (d) => {
      const src = d.source as {
        name: string;
        depth: number;
      };
      const tgt = d.target as {
        name: string;
        depth: number;
      };

      const currentFlowId = `${src.name}@${src.depth}→${tgt.name}@${tgt.depth}`;
      return currentFlowId === flowId ? 1 : 0.1;
    });

  svg
    .selectAll<SVGRectElement, any>(`.${diagramId} .node`)
    .transition()
    .duration(DURATION)
    .style("opacity", (d) => {
      const nodeId = `${d.name}@${d.depth}`;
      return nodeId === sourceId || nodeId === targetId
        ? 1
        : 0.1;
    });

  svg
    .selectAll<SVGTextElement, any>(
      `.${diagramId} text.label`
    )
    .transition()
    .duration(DURATION)
    .style("fill-opacity", (d) => {
      const nodeId = `${d.name}@${d.depth}`;
      return nodeId === sourceId || nodeId === targetId
        ? 1
        : 0.1;
    });
};

/**
 * Reset the highlighted flow when user clicks
 * on the same link again or outside the diagram.
 */
export const resetHighlight = (
  svg: SVGSelection,
  diagramId: string
) => {
  svg
    .selectAll(`.${diagramId} .link, .${diagramId} .node`)
    .transition()
    .duration(DURATION)
    .style("opacity", 1);

  svg
    .selectAll(`.${diagramId} text.label`)
    .transition()
    .duration(DURATION)
    .style("opacity", 1)
    .style("fill-opacity", 1)
    .attr("fill", function () {
      return (
        d3.select(this).attr("data-original-fill") || "#000"
      );
    });
};
